/**
 * LocationDetailsStep — Step 4 of the client-onboarding wizard.
 *
 * Collects the restaurant-specific fields required by the strict-Zod
 * `RestaurantSchema` that aren't part of CoPilot's IMerchant shape:
 *   - concept            → `restaurant.concept` (sourced from the
 *                          admin-managed `business_types` collection;
 *                          a sub-category, when present, overwrites it)
 *   - businessTypeGuid   → `restaurant.businessTypeGuid` (FK preserved
 *                          even when `concept` is overwritten by the
 *                          sub-category name)
 *   - hoursMode          → `restaurant.hoursMode`
 *                          (`'fixed'` | `'by_appointment'`)
 *   - public contact email → `restaurant.contactEmail` (with "use owner
 *                          email" toggle defaulting to the owner's email)
 *   - operating hours    → `restaurant.operatingHours` (full week, hidden
 *                          when `hoursMode === 'by_appointment'`)
 *
 * The sub-category dropdown mirrors the admin `RestaurantModal` exactly:
 * it appears whenever the picked business type has at least one
 * `business_sub_categories` row, and writing a sub-category overwrites
 * the `concept` value with the sub-category's name. No new restaurant
 * field, no migration — same convention.
 *
 * The hours-mode toggle exposes the new `Restaurant.hoursMode` enum.
 * When `'by_appointment'`, the editor is hidden and the merchant is
 * marked as taking orders any time (managed via appointments).
 * `operatingHours` is still persisted with defaults underneath so a
 * future switch back to `'fixed'` doesn't lose data.
 *
 * Auto-saves on every change via `useDebouncedSave` (same pattern as
 * the other steps).
 */

import { useEffect, useState } from 'react';
import type { IMerchant, IOnboardingApplication } from '@order-up/models';
import {
  useAuth,
  useGetBusinessSubCategoriesByType,
  useGetBusinessTypes,
} from '@order-up/client/vite';
import { DEFAULT_OPERATING_HOURS } from '@order-up/common';
import { useDebouncedSave } from '../../lib/hooks';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import {
  OperatingHoursEditor,
  type OperatingHoursValue,
} from '../ui/OperatingHoursEditor';
import type { OnboardingApplicationData } from '../../types/onboarding';

export interface LocationDetailsStepProps {
  application: IOnboardingApplication;
}

type LocationPatch = Partial<{
  concept: string;
  businessTypeGuid: string;
  hoursMode: 'fixed' | 'by_appointment';
  contactEmail: string;
  operatingHours: OperatingHoursValue;
}>;

export function LocationDetailsStep({ application }: LocationDetailsStepProps) {
  const { data: authData } = useAuth();
  const ownerEmail =
    (application.applicationData as OnboardingApplicationData).ownership?.owner
      ?.ownerEmail ||
    authData?.authUser?.email ||
    '';

  const { debouncedSave } = useDebouncedSave({
    applicationId: application.id,
    currentStep: application.currentStep,
  });

  const { data: businessTypes, isLoading: typesLoading } = useGetBusinessTypes();

  const appData = application.applicationData as OnboardingApplicationData;
  const locationData = appData.restaurant ?? {};

  // Local state — mirrors the admin RestaurantModal pattern: track the
  // business-type guid alongside `concept` so we can preserve the FK even
  // when a sub-category overwrites the concept text.
  const [concept, setConcept] = useState<string>(locationData.concept ?? '');
  const [businessTypeGuid, setBusinessTypeGuid] = useState<string>(
    locationData.businessTypeGuid ?? '',
  );
  const [hoursMode, setHoursMode] = useState<'fixed' | 'by_appointment'>(
    locationData.hoursMode ?? 'fixed',
  );
  const [contactEmail, setContactEmail] = useState<string>(
    locationData.contactEmail ?? '',
  );
  // The "Use my account email" checkbox defaults OFF on a fresh draft.
  // Only enable it when the saved contactEmail actually matches the
  // owner email (i.e. the user previously turned it on and saved).
  // Pre-fix this defaulted to `true` whenever contactEmail was empty,
  // which silently disabled the email input AND pre-filled it with the
  // owner email — the user had no idea why their typed email kept
  // disappearing and why the field was greyed out.
  const [useOwnerEmail, setUseOwnerEmail] = useState<boolean>(() => {
    if (!locationData.contactEmail) return false;
    return locationData.contactEmail === ownerEmail;
  });
  const [operatingHours, setOperatingHours] = useState<OperatingHoursValue>(
    locationData.operatingHours ?? { ...DEFAULT_OPERATING_HOURS },
  );

  // Sub-categories are fetched only when a business-type guid is selected;
  // the hook is internally `enabled: !!businessTypeGuid`. Same source the
  // admin RestaurantModal reads.
  const { data: subCategories } = useGetBusinessSubCategoriesByType(
    businessTypeGuid,
  );
  const selectedBusinessType = (businessTypes ?? []).find(
    (bt) => bt.guid === businessTypeGuid,
  );
  const hasSubCategories = (subCategories ?? []).length > 0;

  /** Persist the current local state as a single saveDraft patch. */
  const persist = (next: LocationPatch) => {
    const merged: OnboardingApplicationData['restaurant'] = {
      concept: next.concept ?? concept,
      businessTypeGuid: next.businessTypeGuid ?? businessTypeGuid,
      hoursMode: next.hoursMode ?? hoursMode,
      contactEmail: next.contactEmail ?? contactEmail,
      operatingHours: next.operatingHours ?? operatingHours,
    };
    const updates: Partial<OnboardingApplicationData> = {
      restaurant: merged,
    };
    debouncedSave(updates as Partial<IMerchant>);
  };

  /**
   * Business-type Select onChange. The Select stores the type's `guid`
   * (stable, decoupled from the display name). We do NOT eagerly seed
   * `concept` with the business-type name — instead `concept` stays
   * empty here, which keeps the `isStepValid` Next-button gate disabled
   * until either:
   *   (a) the sub-category effect below populates `concept` with the
   *       picked sub-category name (when sub-categories exist), or
   *   (b) the no-subcategories effect below populates `concept` with
   *       the business-type's name (when there are none).
   *
   * Pre-W10 the concept was set to the type name immediately on type
   * selection, which made Next enable BEFORE the user picked a
   * sub-category — the Retail Category dropdown could be left at
   * "Select a category" and the user could still proceed (per screenshot).
   */
  const handleBusinessTypeChange = (guid: string) => {
    setBusinessTypeGuid(guid);
    setConcept('');
    persist({ businessTypeGuid: guid, concept: '' });
  };

  /**
   * Once the sub-categories query settles, decide whether `concept`
   * should auto-fill with the business-type name (no sub-categories
   * exist) or stay empty (sub-categories exist — user must pick one).
   *
   * This effect only fires AFTER `businessTypeGuid` is set AND the
   * sub-category query has resolved (its data array is defined). It's
   * idempotent: if `concept` is already populated (e.g. user reloaded
   * a saved draft), we don't overwrite.
   */
  useEffect(() => {
    if (!businessTypeGuid) return;
    if (subCategories === undefined) return; // still loading
    if (concept) return; // user-entered or already-saved
    if (subCategories.length === 0) {
      // No sub-categories — fall back to the business type name. This is
      // a one-shot seed gated by `concept` already being empty above; the
      // lint rule flags setState in effects on principle, but here the
      // effect is the canonical "derive state from a settled async query"
      // pattern and won't loop.
      const bt = (businessTypes ?? []).find((b) => b.guid === businessTypeGuid);
      const fallback = bt?.name ?? '';
      if (fallback) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setConcept(fallback);
        persist({ concept: fallback });
      }
    }
    // When sub-categories exist, leave concept empty and let
    // `handleSubCategoryChange` populate it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTypeGuid, subCategories, businessTypes]);

  const handleSubCategoryChange = (subCategoryName: string) => {
    setConcept(subCategoryName);
    persist({ concept: subCategoryName });
  };

  const handleHoursModeToggle = (byAppointment: boolean) => {
    const next: 'fixed' | 'by_appointment' = byAppointment
      ? 'by_appointment'
      : 'fixed';
    setHoursMode(next);
    persist({ hoursMode: next });
  };

  const handleEmailChange = (value: string) => {
    setContactEmail(value);
    setUseOwnerEmail(value === ownerEmail);
    persist({ contactEmail: value });
  };

  const handleUseOwnerEmailToggle = (checked: boolean) => {
    setUseOwnerEmail(checked);
    const next = checked ? ownerEmail : '';
    setContactEmail(next);
    persist({ contactEmail: next });
  };

  const handleHoursChange = (next: OperatingHoursValue) => {
    setOperatingHours(next);
    persist({ operatingHours: next });
  };

  return (
    <div className="space-y-8">
      {/* Business type */}
      <div className="space-y-2">
        <Select
          label="Business Type"
          value={businessTypeGuid}
          onChange={(e) => handleBusinessTypeChange(e.target.value)}
          required
          disabled={typesLoading}
        >
          <option value="">
            {typesLoading ? 'Loading types…' : 'Select a business type'}
          </option>
          {(businessTypes ?? []).map((bt) => (
            <option key={bt.docId} value={bt.guid}>
              {bt.name}
            </option>
          ))}
        </Select>
        <p className="text-xs text-zinc-500">
          The high-level category that best describes the business. Used for
          menu templates, search categorisation, and analytics. You can change
          this later in Management → Settings.
        </p>
      </div>

      {/* Sub-category — only renders when the chosen business type has any.
          Mirrors apps/admin/src/components/admin/RestaurantModal.tsx. */}
      {hasSubCategories && (
        <div className="space-y-2">
          <Select
            label={`${selectedBusinessType?.name ?? 'Business'} Category`}
            value={concept}
            onChange={(e) => handleSubCategoryChange(e.target.value)}
            required
          >
            <option value="">Select a category</option>
            {(subCategories ?? []).map((sc) => (
              <option key={sc.guid} value={sc.name}>
                {sc.name}
              </option>
            ))}
          </Select>
          <p className="text-xs text-zinc-500">
            Pick the category that best fits the business within{' '}
            {selectedBusinessType?.name ?? 'this type'}.
          </p>
        </div>
      )}

      {/* Public contact email */}
      <div className="space-y-2">
        <Input
          label="Public Contact Email"
          type="email"
          value={contactEmail}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder="hello@yourbusiness.com"
          required
          autoComplete="email"
          disabled={useOwnerEmail}
        />
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={useOwnerEmail}
            onChange={(e) => handleUseOwnerEmailToggle(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
          Use my account email ({ownerEmail || 'no owner email yet'})
        </label>
        <p className="text-xs text-zinc-500">
          Customers will see this on receipts and the public listing — keep it
          separate from your owner email if you want to route customer support
          somewhere else.
        </p>
      </div>

      {/* Operating hours — gated by `hoursMode`. */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-medium text-zinc-200">Operating Hours</h3>
          <p className="text-xs text-zinc-500">
            We&apos;ve seeded sensible defaults — toggle a day closed or adjust
            hours as needed. You can fine-tune holiday hours later.
          </p>
        </div>

        <label className="flex items-start gap-3 rounded-lg border border-white/5 bg-zinc-900/40 p-3 text-sm text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={hoursMode === 'by_appointment'}
            onChange={(e) => handleHoursModeToggle(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
          <span className="flex-1">
            <span className="font-medium">
              This business doesn&apos;t have fixed hours
            </span>
            <span className="ml-1 text-xs text-zinc-500">
              (by appointment, on-call, or contractor)
            </span>
          </span>
        </label>

        {hoursMode === 'by_appointment' ? (
          <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
            <p className="text-sm text-blue-200/80">
              We&apos;ll mark this business as <strong>by appointment</strong>.
              Orders can be placed any time and the merchant manages
              availability. You can switch to fixed hours later in
              Management&nbsp;→&nbsp;Settings — your seeded weekly defaults are
              kept underneath.
            </p>
          </div>
        ) : (
          <OperatingHoursEditor
            value={operatingHours}
            onChange={handleHoursChange}
          />
        )}
      </div>

      {/* Info callout */}
      <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
        <p className="text-sm text-blue-200/80">
          <span className="font-medium text-blue-300">Heads up:</span>
          {' '}These details create your first restaurant location. You can add
          more locations and override per-location settings from the management
          app once you&apos;re signed in.
        </p>
      </div>
    </div>
  );
}
